import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, CloudOff, Loader2, MessageCircle, Plus, Star, Timer, Wand2 } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { AppButton, EmptyState } from "@/components/AppUI";
import { ShareButton } from "@/components/ShareButton";
import { basePrice, euro, fileUrl, isPromoToday, minPrice, priceFor, promoOf, promoWhen, sizesOf } from "@/lib/pizzeria";

export const LOGO = "/images/logo-400-gradi.jpg";

const BrandHeader = () => (
  <div className="flex items-center gap-3 px-4 pt-3 pb-1">
    <img src={LOGO} alt="Logo 400 Gradi" className="h-11 w-11 rounded-full object-cover" data-testid="menu-logo" />
    <div className="flex-1 min-w-0">
      <p className="text-xl font-medium leading-tight">Pizzeria 400° Gradi</p>
      <p className="text-xs" style={{ color: "var(--muted-c)" }}>Masi dal 1983 · Asporto</p>
    </div>
    <Link to="/assistente" className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "var(--brand-3)", color: "var(--brand)" }} aria-label="Assistente" data-testid="menu-assistant-button"><MessageCircle className="h-5 w-5" /></Link>
    <ShareButton variant="ghost" label="Condividi" className="h-9 px-3 rounded-full text-xs" />
  </div>
);

const Hero = () => (
  <div className="relative h-[200px] rounded-[20px] overflow-hidden flex flex-col justify-end" style={{ background: "var(--surface-3)" }} data-testid="menu-hero">
    <img src="/images/app-hero.jpg" alt="Pizza nel forno" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0" style={{ background: "linear-gradient(transparent, rgba(0,0,0,.75))" }} />
    <img src={LOGO} alt="" className="absolute top-4 left-4 h-16 w-16 rounded-full border-2 border-white object-cover" />
    <div className="relative p-4 text-white">
      <p className="text-2xl font-medium">Cotta nel forno a 400°</p>
      <p className="text-sm opacity-85">Ordina ora e ritira in pizzeria all'orario che preferisci</p>
    </div>
  </div>
);

const daysLeft = (ends) => Math.ceil((new Date(`${ends}T23:59:59`) - new Date()) / 86400000);

const FeaturedBanner = () => {
  const { featured, products, deals } = useShop();
  const p = featured && (products || []).find((x) => x.id === featured.product_id);
  if (!p) return null;
  const size = sizesOf(p)[0] || null;
  const left = featured.ends_on ? daysLeft(featured.ends_on) : null;
  return (
    <Link to={`/product/${p.id}`} className="press relative h-[220px] rounded-[20px] overflow-hidden flex flex-col justify-end" style={{ border: "2px solid var(--brand)" }} data-testid="featured-banner">
      {p.image && <img src={fileUrl(p.image)} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0" style={{ background: "linear-gradient(transparent, rgba(0,0,0,.8))" }} />
      <span className="absolute top-3 left-3 flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-medium tracking-wide" style={{ background: "var(--brand)", color: "var(--on-brand)" }}><Star className="h-3 w-3 fill-current" />PIZZA DEL MESE · -{featured.discount_percent}%</span>
      <div className="relative p-4 text-white space-y-1">
        <p className="text-2xl font-medium" data-testid="featured-name">{p.name}</p>
        <p className="flex items-baseline gap-2" data-testid="featured-price"><span className="text-sm opacity-60 line-through">{euro(basePrice(p, size))}</span><span className="text-xl font-medium">{euro(priceFor(p, size, deals))}</span></p>
        {left != null && <p className="flex items-center gap-1 text-xs opacity-85" data-testid="featured-ends"><Timer className="h-3.5 w-3.5" />{left <= 1 ? "Ultimo giorno!" : `Ancora ${left} giorni · fino al ${new Date(featured.ends_on).toLocaleDateString("it-IT")}`}</p>}
      </div>
    </Link>
  );
};

const PromoBanners = () => {
  const { promos, products } = useShop();
  const list = promos.filter(isPromoToday);
  if (!list.length) return null;
  return list.map((pr) => {
    const names = (products || []).filter((p) => pr.product_ids?.includes(p.id)).map((p) => p.name);
    const what = pr.kind === "order" ? `Su tutto l'ordine${pr.min_total ? ` da ${euro(pr.min_total)}` : ""}` : `Su ${names.slice(0, 4).join(", ")}${names.length > 4 ? ` e altre ${names.length - 4}` : ""}`;
    const first = pr.kind === "products" && (products || []).find((p) => pr.product_ids?.includes(p.id));
    return (
      <Link key={pr.id || pr.name} to={first ? `/product/${first.id}` : "/carrello"} className="press flex items-center gap-3 p-4 rounded-[20px]" style={{ background: "var(--brand)", color: "var(--on-brand)" }} data-testid={`promo-banner-${pr.id || pr.name}`}>
        <span className="h-14 w-14 rounded-full grid place-items-center text-base font-extrabold shrink-0" style={{ background: "var(--on-brand)", color: "var(--brand)" }}>-{pr.percent}%</span>
        <span className="min-w-0 flex-1"><span className="block text-[11px] font-bold tracking-widest opacity-90">PROMOZIONE</span><span className="block text-lg font-bold leading-tight">{pr.name}</span><span className="block text-[13px] opacity-95">{what}</span>{promoWhen(pr) && <span className="block text-xs opacity-85">{promoWhen(pr)}</span>}</span>
        <ChevronRight className="h-5 w-5 shrink-0" />
      </Link>
    );
  });
};

const BuilderCard = () => (
  <Link to="/builder" className="press flex items-center gap-3 p-4 rounded-[20px]" style={{ background: "var(--brand)", color: "var(--on-brand)" }} data-testid="menu-builder-card">
    <Wand2 className="h-7 w-7 shrink-0" />
    <span className="flex-1"><span className="block text-[17px] font-bold">Crea la tua pizza</span><span className="block text-[13px] opacity-90">Rotonda, teglia o mezza: scegli base, gusti e ingredienti</span></span>
    <ChevronRight className="h-6 w-6" />
  </Link>
);

const ProductCard = ({ p }) => {
  const { deals: featured } = useShop();
  const nav = useNavigate();
  const pct = promoOf(p, featured);
  const sizes = sizesOf(p);
  return (
    <button onClick={() => nav(`/product/${p.id}`)} className="press fade-in text-left rounded-[20px] overflow-hidden" style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)", boxShadow: "0 2px 8px rgba(0,0,0,.05)" }} data-testid={`product-card-${p.id}`}>
      {p.image && <div className="relative"><img src={fileUrl(p.image)} alt={p.name} className="w-full h-[150px] object-cover" style={{ background: "var(--surface-3)" }} loading="lazy" />{pct > 0 && <span className="absolute top-3 left-3 rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: "var(--brand)", color: "var(--on-brand)" }} data-testid={`product-promo-${p.id}`}>-{pct}%</span>}</div>}
      <div className="flex items-end gap-3 p-4">
        <div className="flex-1 min-w-0 space-y-1">
          <p className="text-base font-medium">{p.name}</p>
          {p.description && <p className="text-sm leading-5 line-clamp-2" style={{ color: "var(--muted-c)" }}>{p.description}</p>}
          <p className="text-base font-medium mt-1" data-testid={`product-price-${p.id}`}>
            {sizes.length > 1 && <span className="text-xs font-normal" style={{ color: "var(--muted-c)" }}>da </span>}
            {pct > 0 && <span className="text-sm font-normal line-through mr-1.5" style={{ color: "var(--muted-c)" }}>{euro(Math.min(...(sizes.length ? sizes.map((s) => basePrice(p, s)) : [p.price || 0])))}</span>}
            {euro(minPrice(p, featured))}
          </p>
        </div>
        <span className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--brand)", color: "var(--on-brand)" }}><Plus className="h-5 w-5" /></span>
      </div>
    </button>
  );
};

export default function MenuPage() {
  const { products, categories, settings, error, reload } = useShop();
  const [cat, setCatState] = useState(() => sessionStorage.getItem("p400_cat") || "");
  const setCat = (k) => {
    sessionStorage.setItem("p400_cat", k);
    setCatState(k);
  };
  const groups = useMemo(() => categories.filter((c) => (products || []).some((p) => p.category === c.key)), [categories, products]);

  useEffect(() => {
    if (groups[0] && !groups.some((g) => g.key === cat)) {
      setCatState(groups[0].key);
    }
  }, [groups, cat]);

  const list = (products || []).filter((p) => p.category === cat);
  return (
    <div className="flex-1" data-testid="menu-page">
      <div className="sticky top-0 z-30 pb-2" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border-c)" }}>
        <BrandHeader />
        <div className="h-14 flex items-center gap-2 px-4 overflow-x-auto no-scrollbar">
          {groups.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`category-chip-${c.key}`} className="h-9 px-4 rounded-full text-sm font-medium shrink-0 whitespace-nowrap transition-colors"
              style={cat === c.key ? { background: "var(--brand-2)", color: "var(--on-brand-2)", border: "1px solid var(--brand-2)" } : { background: "var(--surface-2)", border: "1px solid var(--border-c)" }}>{c.label}</button>
          ))}
        </div>
      </div>
      {products === null ? (
        <div className="flex flex-col items-center gap-2 py-24" data-testid="menu-loading"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--brand)" }} /><p className="text-sm" style={{ color: "var(--muted-c)" }}>Caricamento menù...</p></div>
      ) : error ? (
        <EmptyState icon={CloudOff} title="Impossibile caricare il menù" subtitle="Controlla la connessione e riprova." testId="menu-error"><AppButton title="Riprova" onClick={reload} testId="menu-retry-button" /></EmptyState>
      ) : (
        <div className="p-4 pb-6 flex flex-col gap-4">
          {settings?.orders_paused && <p className="rounded-2xl p-3.5 text-sm font-medium" style={{ background: "var(--brand-3)", color: "var(--on-brand-3)" }} data-testid="menu-orders-paused">Al momento non accettiamo ordini dall'app. Puoi guardare il menù e chiamarci per ordinare.</p>}
          <PromoBanners />
          <Hero />
          <FeaturedBanner />
          {settings?.builder?.enabled !== false && <BuilderCard />}
          {list.map((p) => <ProductCard key={p.id} p={p} />)}
          {!list.length && <p className="text-center text-sm py-10" style={{ color: "var(--muted-c)" }} data-testid="menu-empty">Nessun prodotto disponibile al momento.</p>}
        </div>
      )}
    </div>
  );
}
